import { useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import type { Role, User } from "@ld-scorecard/shared";
import { useApi } from "../api";
import { useMe } from "../App";

export default function AdminPage() {
  const api = useApi();
  const me = useMe();
  const [users, setUsers] = useState<User[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<Role>("panelist");
  const [busy, setBusy] = useState(false);

  const load = () =>
    api
      .listUsers()
      .then(setUsers)
      .catch((e) => setError(String(e.message ?? e)));

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  const add = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.addUser({ email: email.trim(), role, name: name.trim() || undefined });
      setEmail("");
      setName("");
      setRole("panelist");
      await load();
    } catch (e) {
      setError(String((e as Error).message));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (target: string) => {
    if (!confirm(`Remove ${target} from the allowlist?`)) return;
    setError(null);
    try {
      await api.removeUser(target);
      await load();
    } catch (e) {
      setError(String((e as Error).message));
    }
  };

  return (
    <>
      <h3 className="mb-3">Panel allowlist</h3>
      <p className="text-muted">
        Only people on this list can sign in. Add panelists and admins here.
      </p>

      <Card className="mb-4 shadow-sm">
        <Card.Header className="fw-semibold">Add someone</Card.Header>
        <Card.Body>
          <Row className="g-2 align-items-end">
            <Col md={5}>
              <Form.Label className="small">Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="person@launchdarkly.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Col>
            <Col md={3}>
              <Form.Label className="small">Name (optional)</Form.Label>
              <Form.Control value={name} onChange={(e) => setName(e.target.value)} />
            </Col>
            <Col md={2}>
              <Form.Label className="small">Role</Form.Label>
              <Form.Select value={role} onChange={(e) => setRole(e.target.value as Role)}>
                <option value="panelist">panelist</option>
                <option value="admin">admin</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Button className="w-100" disabled={!email.trim() || busy} onClick={add}>
                Add
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {error && <Alert variant="danger">{error}</Alert>}
      {!users && !error && <Spinner animation="border" />}

      {users && (
        <Card className="shadow-sm">
          <Table responsive className="mb-0 align-middle">
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Role</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.email}>
                  <td>{u.email}</td>
                  <td>{u.name ?? "—"}</td>
                  <td>
                    <Badge bg={u.role === "admin" ? "info" : "secondary"} text={u.role === "admin" ? "dark" : undefined}>
                      {u.role}
                    </Badge>
                  </td>
                  <td className="text-end">
                    <Button
                      size="sm"
                      variant="outline-danger"
                      disabled={u.email === me?.email}
                      onClick={() => void remove(u.email)}
                    >
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}
    </>
  );
}
